import { Button } from "@/dentlab/src/components/ui/button";
import { X } from "lucide-react";

interface OnboardingStepProps {
    children: React.ReactNode;
    stepNumber: number;
    totalSteps: number;
    stepTitle: string;
    stepDescription?: string;
    onButtonClick: () => void;
    buttonText: string;
    buttonDisabled: boolean;
    loading: boolean;
    errorMessage: string | null;
    onErrorMessageClose: () => void;
    canGoBack: boolean;
    onGoBackClick?: () => void;
    canSkip?: boolean;
    onSkip?: () => void;
    rightPanel?: React.ReactNode;
    rightPanelStyle?: React.CSSProperties;
}

export const OnboardingStep: React.FC<OnboardingStepProps> = ({
    children,
    stepNumber,
    totalSteps,
    stepTitle,
    stepDescription,
    onButtonClick,
    buttonText,
    buttonDisabled,
    loading,
    errorMessage,
    onErrorMessageClose,
    canGoBack,
    onGoBackClick,
    canSkip,
    onSkip,
    rightPanel,
    rightPanelStyle,
}) => {
    return (
        <div className="h-screen w-screen flex">
            {loading && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
                    </div>
                </div>
            )}

            <div className="w-full lg:w-1/2 bg-white flex flex-col">
                <div className="flex items-center justify-between px-6 pt-6">
                    <img src="/logos/dentlab-logo.svg" alt="dentLAB" className="h-7" />
                    <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-500">{stepNumber}/{totalSteps}</span>
                        <div className="flex items-center gap-1.5">
                            {Array.from({ length: totalSteps }, (_, i) => (
                                <div
                                    key={i}
                                    className="h-1.5 w-8 rounded-full"
                                    style={{ backgroundColor: i < stepNumber ? "#8db599" : "#e5e7eb" }}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                <div className="flex-1 flex flex-col justify-center px-6 lg:px-16 py-8">
                    <div className="max-w-md mx-auto w-full">
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                            {stepTitle}
                        </h2>

                        {stepDescription && (
                            <p className="text-sm sm:text-base text-gray-600 mb-6 leading-relaxed">
                                {stepDescription}
                            </p>
                        )}

                        {errorMessage && (
                            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start justify-between">
                                <div className="flex items-start">
                                    <div className="flex-shrink-0">
                                        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm text-red-800">{errorMessage}</p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => onErrorMessageClose()}
                                    className="flex-shrink-0 ml-4 text-red-400 hover:text-red-600"
                                    aria-label="Fehler schließen"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>
                        )}

                        <form className="space-y-4">
                            {children}

                            <div className="flex items-center justify-between pt-2">
                                <div className="flex items-center gap-2">
                                    {canGoBack && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            onClick={onGoBackClick}
                                            disabled={loading}
                                            className="h-9 sm:h-10 text-gray-600"
                                        >
                                            Zurück
                                        </Button>
                                    )}
                                    {canSkip && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            onClick={onSkip}
                                            disabled={loading}
                                            className="h-9 sm:h-10 text-gray-600"
                                        >
                                            Überspringen
                                        </Button>
                                    )}
                                </div>

                                <Button
                                    type="button"
                                    onClick={onButtonClick}
                                    disabled={buttonDisabled || loading}
                                    className="h-9 sm:h-10 px-6"
                                >
                                    {buttonText}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <div className="hidden lg:flex w-1/2 bg-gray-100 items-center justify-center p-16" style={rightPanelStyle}>
                {rightPanel}
            </div>
        </div>
    );
};
